import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { detectToolsOrigin } from "../../../utils/helper.js";

import LeadGateModal from "../Modals/LeadGateModal.jsx";
import SuccessModal from "../Modals/SuccessModal.jsx";
import ConfirmSendResultsModal from "../Modals/ConfirmSendResultsModal.jsx";
import UpdateEmailModal from "../Modals/UpdateEmailModal.jsx";

import "../calculator.scss";

const PROXY_URL =
  window.__UK_PROXY_URL__ ||
  `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

// ✅ User requested exact disclaimer text (no extra text)
const selfEmploymentDisclaimerText = (year) =>
  `This calculator provides estimates based on ${year} self-employment tax rules. Actual tax liability may vary based on your specific situation, deductions, and other factors. Consult Uncle Kam's tax professionals for personalized advice`;

function SelfEmploymentCalculator({
  year = 2025,
  calculatorName = "Self Employment Tax Calculator",
  endpoint = PROXY_URL, // kept for compatibility
}) {
  // ✅ FIX: keep "filingStatus" as the full label (same as other calculators)
  const [filingStatus, setFilingStatus] = useState("Single");

  const [otherIncome, setOtherIncome] = useState("");
  const [grossIncome, setGrossIncome] = useState("");
  const [businessExpenses, setBusinessExpenses] = useState("");
  const [quarterlyPayments, setQuarterlyPayments] = useState("");

  // ✅ results should NOT update automatically
  const [results, setResults] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  // ✅ keep last computed JSON payload for emailing
  const latestPayloadRef = useRef({ inputs_json: null, results_json: null });

  const resultsRef = useRef(null);

  // ----- Constants -----
  const SOCIAL_SECURITY_RATE = 0.124; // 12.4%
  const MEDICARE_RATE = 0.029; // 2.9%

  // For UX + assumptions card
  const SE_ADJUSTMENT_FACTOR = 0.9235; // 92.35%
  const MARGINAL_TAX_RATE = 0.24; // Assumed 24%

  const WAGE_BASE_BY_YEAR = {
    2025: 176_100,
    2026: 176_100,
  };

  const SOCIAL_SECURITY_WAGE_BASE =
    WAGE_BASE_BY_YEAR[year] ?? WAGE_BASE_BY_YEAR[2025];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  };

  // ✅ Only compute what you actually display + send
  const computePayload = () => {
    const grossIncomeNum = parseFloat(grossIncome) || 0;
    const businessExpensesNum = parseFloat(businessExpenses) || 0;
    const otherIncomeNum = parseFloat(otherIncome) || 0;
    const quarterlyPaymentsNum = parseFloat(quarterlyPayments) || 0;

    const netProfit = grossIncomeNum - businessExpensesNum;
    const netEarnings = netProfit * SE_ADJUSTMENT_FACTOR;

    // Social Security taxable earnings capped by remaining wage base after W-2 wages
    const socialSecurityWageBaseRemaining = Math.max(
      0,
      SOCIAL_SECURITY_WAGE_BASE - otherIncomeNum
    );

    const socialSecurityTaxableEarnings = Math.min(
      netEarnings,
      socialSecurityWageBaseRemaining
    );

    const socialSecurityTax =
      socialSecurityTaxableEarnings * SOCIAL_SECURITY_RATE;
    const medicareTax = netEarnings * MEDICARE_RATE;

    const totalSeTax = socialSecurityTax + medicareTax;

    const deduction = totalSeTax * 0.5;
    const taxSavings = deduction * MARGINAL_TAX_RATE;

    const remainingTaxDue = Math.max(0, totalSeTax - quarterlyPaymentsNum);

    // ✅ inputs_json: only what the UI actually collects/displays
    const inputs_json = {
      filingStatus, // ✅ full label
      otherIncome: otherIncomeNum,
      grossIncome: grossIncomeNum,
      businessExpenses: businessExpensesNum,
      quarterlyPayments: quarterlyPaymentsNum,
      year: Number(year),
      calculatorName,
    };

    // ✅ results_json: only what the UI actually displays
    const results_json = {
      netEarnings,
      totalSeTax,
      socialSecurityTax,
      medicareTax,
      deduction,
      taxSavings,
      remainingTaxDue,
    };

    // ✅ keep disclaimer with payload too (so modals can use it consistently)
    const pdf_notes = {
      disclaimer: selfEmploymentDisclaimerText(year),
    };

    return { inputs_json, results_json, pdf_notes };
  };

  // ============ GATING (same shared key + 24h TTL) ============
  const UNK_GATE_KEY = "unk_calc_unlock_v1";
  const UNK_GATE_TTL_MS = 24 * 60 * 60 * 1000;

  const readUnlock = () => {
    try {
      const raw = localStorage.getItem(UNK_GATE_KEY);
      if (!raw) return { unlocked: false, email: "" };
      const parsed = JSON.parse(raw);
      const expiresAt = Number(parsed?.expiresAt || 0);
      const email = String(parsed?.email || "");
      if (!expiresAt || Date.now() > expiresAt) {
        localStorage.removeItem(UNK_GATE_KEY);
        return { unlocked: false, email: "" };
      }
      return { unlocked: true, email };
    } catch {
      return { unlocked: false, email: "" };
    }
  };

  const writeUnlock = ({ email }) => {
    const payload = {
      expiresAt: Date.now() + UNK_GATE_TTL_MS,
      email: String(email || ""),
    };
    localStorage.setItem(UNK_GATE_KEY, JSON.stringify(payload));
  };

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  const [unlocked, setUnlocked] = useState(false);
  const [storedEmail, setStoredEmail] = useState("");

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);

  const [sendingResults, setSendingResults] = useState(false);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(() => params.has("qa_mode"), [params]);

  useEffect(() => {
    const gate = readUnlock();
    setUnlocked(gate.unlocked);
    setStoredEmail(gate.email || "");
  }, []);

  // ✅ if user edits inputs after calculating, invalidate results + email payload
  useEffect(() => {
    setHasCalculated(false);
    setResults(null);
    latestPayloadRef.current = { inputs_json: null, results_json: null, pdf_notes: null };
  }, [filingStatus, otherIncome, grossIncome, businessExpenses, quarterlyPayments, year]);

  const scrollToResults = () => {
    const el = resultsRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const openGateFlow = () => {
    if (unlocked) {
      setShowConfirmModal(true);
      return;
    }
    setShowLeadModal(true);
  };

  const handleLeadSuccess = ({ email }) => {
    writeUnlock({ email });
    setUnlocked(true);
    setStoredEmail(email || "");
    setShowLeadModal(false);
    setShowSuccessModal(true);
  };

  const handleSuccessViewResults = () => {
    setShowSuccessModal(false);
    scrollToResults();
  };

  // ✅ Send results via proxy + pdf_notes.disclaimer
  const sendResultsToEmail = async (emailToSend) => {
    const payload = latestPayloadRef.current;
    if (!payload?.inputs_json || !payload?.results_json) return null;

    setSendingResults(true);
    try {
      const resp = await axios.post(
        PROXY_URL,
        {
          path: "/public/send-results",
          method: "POST",
          raw: true,
          qa_mode: qa,
          data: {
            action: "send_results_email",
            email: emailToSend,
            calculator_name: calculatorName,
            calculator_year: String(year),
            page_url: pageUrl,
            timestamp_utc: new Date().toISOString(),
            inputs_json: payload.inputs_json,
            results_json: payload.results_json,
            pdf_notes: {
              disclaimer: selfEmploymentDisclaimerText(year),
            },
            schema_version: 1,
          },
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      if (resp?.data?.success === false) {
        throw new Error(resp?.data?.message || "Failed to send results email");
      }

      setShowConfirmModal(false);
      setShowUpdateEmailModal(false);
      scrollToResults();
      return resp.data;
    } catch (err) {
      console.error("sendResultsToEmail error:", err);
      return null;
    } finally {
      setSendingResults(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!storedEmail) {
      setShowConfirmModal(false);
      setShowUpdateEmailModal(true);
      return;
    }
    await sendResultsToEmail(storedEmail);
  };

  const handleChangeEmail = () => {
    setShowConfirmModal(false);
    setShowUpdateEmailModal(true);
  };

  const handleUpdateEmailAndSend = async (newEmail) => {
    const e = String(newEmail || "").trim();
    writeUnlock({ email: e });
    setStoredEmail(e);
    setUnlocked(true);
    await sendResultsToEmail(e);
  };

  // ✅ compute + show results ONLY on click
  const handleViewResultsClick = () => {
    const payload = computePayload();
    latestPayloadRef.current = payload;

    setResults(payload.results_json);
    setHasCalculated(true);

    openGateFlow();
    setTimeout(scrollToResults, 50);
  };

  // ✅ if they click overlay before calc, calc first so the preview becomes real
  const runCalcIfPossible = () => {
    const payload = computePayload();
    latestPayloadRef.current = payload;
    setResults(payload.results_json);
    setHasCalculated(true);
  };

  return (
    <>
      <div className="min-vh-100">
        <div className="container py-5">
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="card border-success mb-4">
                <div className="card-header bg-success text-white">
                  <i className="bi bi-people-fill me-2"></i> Personal Information
                </div>
                <div className="card-body">
                  <label className="form-label">Filing Status</label>

                  {/* ✅ FIXED: values are full labels (no single/marriedJoint crap) */}
                  <select
                    className="form-select border-success mb-3"
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e.target.value)}
                  >
                    <option value="Single">Single</option>
                    <option value="Head of Household">Head of Household</option>
                    <option value="Married Filing Jointly">Married Filing Jointly</option>
                    <option value="Married Filing Separately">Married Filing Separately</option>
                    <option value="Qualifying Widow(er)">Qualifying Widow(er)</option>
                  </select>

                  <label className="form-label">Other W-2 Income (if any)</label>
                  <input
                    type="number"
                    className="form-control border-success"
                    value={otherIncome}
                    onChange={(e) => setOtherIncome(e.target.value)}
                  />
                  <div className="form-text">
                    Income from employment that already had Social Security taxes withheld
                  </div>
                </div>
              </div>

              <div className="card border-warning mb-4">
                <div className="card-header bg-warning text-white">
                  <i className="bi bi-cash-stack me-2"></i> Self-Employment Income & Expenses
                </div>
                <div className="card-body">
                  <label className="form-label">Gross Self-Employment Income</label>
                  <input
                    type="number"
                    className="form-control border-warning mb-3"
                    value={grossIncome}
                    onChange={(e) => setGrossIncome(e.target.value)}
                  />
                  <div className="form-text mb-3">
                    Total income from freelancing, contracting, or business activities
                  </div>

                  <label className="form-label">Allowable Business Expenses</label>
                  <input
                    type="number"
                    className="form-control border-warning"
                    value={businessExpenses}
                    onChange={(e) => setBusinessExpenses(e.target.value)}
                  />
                  <div className="form-text">
                    Deductible expenses like home office, supplies, professional development, etc.
                  </div>
                </div>
              </div>

              <div className="card border-purple mb-4">
                <div className="card-header bg-purple text-white">
                  <i className="bi bi-calculator me-2"></i> Estimated Tax Payments
                </div>
                <div className="card-body">
                  <label className="form-label">Quarterly Payments Made (Optional)</label>
                  <input
                    type="number"
                    className="form-control border-info"
                    value={quarterlyPayments}
                    onChange={(e) => setQuarterlyPayments(e.target.value)}
                  />
                  <div className="form-text">
                    Total estimated tax payments made for the current year
                  </div>
                </div>
              </div>

              <button
                className="btn w-100"
                onClick={handleViewResultsClick}
                style={{
                  background: "#0f8a4c",
                  borderColor: "#0f8a4c",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                View Results
              </button>
            </div>

            {/* ✅ Results column ALWAYS visible */}
            <div className="col-lg-4" ref={resultsRef}>
              <div className="unk-results-wrap rounded-4">
                {!unlocked && <div className="unk-bg-blur" />}

                <div style={{ position: "relative", zIndex: 1 }}>
                  <div className={!unlocked ? "unk-results-blur" : ""}>
                    {/* Results Card */}
                    <div className="card bg-primary text-white" style={{ top: "2rem" }}>
                      <div className="card-header">
                        <i className="bi bi-graph-up-arrow me-2"></i> Your Self-Employment Tax
                      </div>

                      <div className="card-body">
                        {hasCalculated && results ? (
                          <>
                            <div className="d-flex justify-content-between mb-2">
                              <span>Net Earnings from SE:</span>
                              <span className="fw-bold">
                                {formatCurrency(results.netEarnings)}
                              </span>
                            </div>

                            <hr className="border-white" />

                            <div className="d-flex justify-content-between mb-2">
                              <span className="fw-semibold">Total SE Tax Due:</span>
                              <span className="fw-bold fs-5">
                                {formatCurrency(results.totalSeTax)}
                              </span>
                            </div>

                            <div className="small mb-3">
                              <div className="d-flex justify-content-between">
                                <span>Social Security (12.4%):</span>
                                <span>{formatCurrency(results.socialSecurityTax)}</span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span>Medicare (2.9%):</span>
                                <span>{formatCurrency(results.medicareTax)}</span>
                              </div>
                            </div>

                            <hr className="border-white" />

                            <div className="d-flex justify-content-between mb-2">
                              <span>Deduction (Half of SE Tax):</span>
                              <span className="fw-bold">
                                {formatCurrency(results.deduction)}
                              </span>
                            </div>

                            <div className="d-flex justify-content-between mb-2">
                              <span className="small">Estimated Tax Savings:</span>
                              <span className="fw-bold">
                                {formatCurrency(results.taxSavings)}
                              </span>
                            </div>

                            {parseFloat(quarterlyPayments) > 0 && (
                              <>
                                <hr className="border-white" />
                                <div className="d-flex justify-content-between">
                                  <span>Remaining Tax Due:</span>
                                  <span className="fw-bold">
                                    {formatCurrency(results.remainingTaxDue)}
                                  </span>
                                </div>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="d-flex justify-content-between mb-2 opacity-75">
                              <span>Net Earnings from SE:</span>
                              <span className="fw-bold">$—</span>
                            </div>

                            <hr className="border-white" />

                            <div className="d-flex justify-content-between mb-2 opacity-75">
                              <span className="fw-semibold">Total SE Tax Due:</span>
                              <span className="fw-bold fs-5">$—</span>
                            </div>

                            <div className="small mb-3 opacity-75">
                              <div className="d-flex justify-content-between">
                                <span>Social Security (12.4%):</span>
                                <span>$—</span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span>Medicare (2.9%):</span>
                                <span>$—</span>
                              </div>
                            </div>

                            <hr className="border-white" />

                            <div className="d-flex justify-content-between mb-2 opacity-75">
                              <span>Deduction (Half of SE Tax):</span>
                              <span className="fw-bold">$—</span>
                            </div>

                            <div className="d-flex justify-content-between mb-2 opacity-75">
                              <span className="small">Estimated Tax Savings:</span>
                              <span className="fw-bold">$—</span>
                            </div>

                            <div className="text-white-50 small mt-3">
                              Fill inputs and click View Results to generate your numbers.
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {!unlocked && (
                    <div
                      className="unk-results-overlay"
                      onClick={() => {
                        if (!hasCalculated) runCalcIfPossible();
                        openGateFlow();
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          if (!hasCalculated) runCalcIfPossible();
                          openGateFlow();
                        }
                      }}
                      aria-label="Results locked. Open unlock modal."
                    >
                      <div className="unk-overlay-card">
                        <div className="unk-overlay-title">Results Locked</div>
                        <div className="unk-overlay-body">
                          Enter your name and email to view your results instantly.
                        </div>
                        <button type="button" className="unk-overlay-btn">
                          View Results
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ✅ Footer disclaimer (UI) — exact requested text */}
          <div className="mt-4 small text-muted text-center">
            * {selfEmploymentDisclaimerText(year)}
          </div>
        </div>
      </div>

      {/* ✅ Gating Modals */}
      <LeadGateModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSuccess={(payload) => handleLeadSuccess(payload)}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        endpoint={endpoint}
        // ✅ IMPORTANT: pass payloads so lead + email have data
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={latestPayloadRef.current?.inputs_json}
        disclaimer={selfEmploymentDisclaimerText(year)}
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onViewResults={handleSuccessViewResults}
      />

      <ConfirmSendResultsModal
        open={showConfirmModal}
        email={storedEmail}
        onClose={() => setShowConfirmModal(false)}
        onViewResults={() => {
          setShowConfirmModal(false);
          scrollToResults();
        }}
        onSend={handleConfirmSend}
        onChangeEmail={handleChangeEmail}
        sending={sendingResults}
      />

      <UpdateEmailModal
        open={showUpdateEmailModal}
        initialEmail={storedEmail}
        onClose={() => setShowUpdateEmailModal(false)}
        onSend={handleUpdateEmailAndSend}
        sending={sendingResults}
        storedEmail={storedEmail}
        endpoint={PROXY_URL}
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={latestPayloadRef.current?.inputs_json}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        disclaimer={selfEmploymentDisclaimerText(year)}
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />
    </>
  );
}

export default SelfEmploymentCalculator;
